// In production the SPA is served from the same origin that proxies /api/*,
// /auth/*, /oauth/*, /.well-known/*, /mcp/* to API Gateway via CloudFront.
// In dev (vite at localhost:5173), VITE_AUTH_API_URL points at the deployed
// origin so cross-origin fetches work.
const ORIGIN = import.meta.env.VITE_AUTH_API_URL ?? "";

const JWT_KEY = "mcpbuild_jwt";

export function getJwt(): string | null {
  return localStorage.getItem(JWT_KEY);
}

export function setJwt(jwt: string): void {
  localStorage.setItem(JWT_KEY, jwt);
}

export function clearJwt(): void {
  localStorage.removeItem(JWT_KEY);
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

/**
 * Thrown when a gated action hits a Pro-only feature. The server returns
 * HTTP 402 with `{ error: { code: "pro_required", feature, message } }`.
 * Catch this anywhere a gated endpoint is called and surface the upgrade
 * nudge (see `ProUpgradeNudge`). `instanceof ProRequiredError` works, and
 * `.code === "pro_required"` is also set for cross-bundle safety.
 */
export class ProRequiredError extends Error {
  readonly code = "pro_required" as const;
  constructor(
    public feature: string | undefined,
    message: string,
  ) {
    super(message);
    this.name = "ProRequiredError";
  }
}

export function isProRequiredError(e: unknown): e is ProRequiredError {
  return (
    e instanceof ProRequiredError ||
    (typeof e === "object" &&
      e !== null &&
      (e as { code?: unknown }).code === "pro_required")
  );
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const jwt = getJwt();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((init?.headers as Record<string, string>) ?? {}),
  };
  if (jwt) headers["Authorization"] = `Bearer ${jwt}`;

  const res = await fetch(`${ORIGIN}${path}`, { ...init, headers });
  if (!res.ok) {
    const text = await res.text();
    // A gated feature returns 402 with a typed body — surface it so the UI
    // can show the upgrade nudge instead of a raw error string.
    if (res.status === 402) {
      try {
        const body = JSON.parse(text) as {
          error?: { code?: string; feature?: string; message?: string };
        };
        if (body?.error?.code === "pro_required") {
          throw new ProRequiredError(
            body.error.feature,
            body.error.message || "This feature requires Pro.",
          );
        }
      } catch (e) {
        if (e instanceof ProRequiredError) throw e;
        // fall through to generic ApiError on JSON parse failure
      }
    }
    throw new ApiError(res.status, text || res.statusText);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export type App = {
  name: string;
  slug: string;
  icon: string | null;
  accentColor: string | null;
  public?: boolean;
  shareToken?: string | null;
  fetchAllowlist?: string[];
  /** Per-project widget attribution footer (Pro-editable). null => default. */
  footerText?: string | null;
  footerHidden?: boolean;
  createdAt: string;
  updatedAt: string;
};

export type Tier = "free" | "pro" | "lifetime";

export type Me = {
  email: string;
  ownedEntitlements?: string[];
  tier?: Tier;
  createdAt?: string | null;
  lastLoginAt?: string | null;
};

export const getMe = () => api<Me>("/auth/me");

// Stripe Checkout. POST /checkout returns a hosted Checkout URL; redirect the
// browser to it. On return Stripe lands the user back on the SPA with
// `?upgrade=success` or `?upgrade=cancelled`.
export type CheckoutPlan = "pro_monthly" | "pro_annual" | "lifetime";

export async function checkout(plan: CheckoutPlan): Promise<{ url: string }> {
  return api<{ url: string }>("/checkout", {
    method: "POST",
    body: JSON.stringify({ plan }),
  });
}

// Cookie bridge: copy the localStorage JWT into the HttpOnly SSO cookie so a
// later /oauth/authorize (Claude connecting) auto-approves without a fresh
// magic-link email. Best-effort — failures are non-fatal. `credentials:
// "include"` lets the browser store the Set-Cookie (same-origin in prod).
export async function setSsoSession(): Promise<void> {
  const jwt = getJwt();
  if (!jwt) return;
  try {
    await fetch(`${ORIGIN}/auth/session`, {
      method: "POST",
      headers: { Authorization: `Bearer ${jwt}` },
      credentials: "include",
    });
  } catch {
    // best-effort — the email fallback still works if this fails
  }
}

// The reverse cookie bridge (PWA bootstrap): installed iOS PWAs share
// same-origin COOKIES with Safari but NOT localStorage, so a freshly
// installed PWA boots with no stored JWT even though the user signed in via
// Safari. Before concluding signed-out, exchange the HttpOnly SSO cookie for
// a fresh 30-day JWT. Returns the new JWT (already stored via setJwt) or
// null when there's no usable cookie session. Best-effort — a 401 just means
// the user really is signed out.
export async function bootstrapSsoSession(): Promise<string | null> {
  try {
    const res = await fetch(`${ORIGIN}/auth/session`, {
      method: "GET",
      credentials: "include",
    });
    if (!res.ok) return null;
    const body = (await res.json()) as { jwt?: string };
    if (!body.jwt || typeof body.jwt !== "string") return null;
    setJwt(body.jwt);
    return body.jwt;
  } catch {
    return null;
  }
}

// Approve a pending MCP OAuth request from inside the SPA (the user is signed
// in here). Returns the client callback URL to navigate to.
export async function approveOAuth(code: string): Promise<{ redirect: string }> {
  const jwt = getJwt();
  const res = await fetch(`${ORIGIN}/oauth/approve`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
    },
    credentials: "include",
    body: JSON.stringify({ code }),
  });
  if (!res.ok) throw new ApiError(res.status, (await res.text()) || res.statusText);
  return res.json() as Promise<{ redirect: string }>;
}

export type AppTool = {
  toolSlug: string;
  description: string | null;
  type: string;
  displayMode: string;
  widgetHash: string | null;
  htmlUrl: string | null;
  bundleUrl: string | null;
  updatedAt: string | null;
  /** True when the bundle exists in S3 — broken widgets get filtered out
   * of the Inbox gallery so users don't see ghost cards. */
  renderable: boolean;
  /** Whether this tool is exposed on the project's MCP endpoint. */
  exposed: boolean;
  /** The widget this tool renders (same widget can back tools in many
   * projects). Null for ephemeral inbox rows. */
  widgetId: string | null;
  /** Natural-language phrases a user would say to trigger this tool. Appended
   * to the served MCP description for LLM routing; surfaced as one-click
   * "Try this prompt" pills on the project page. */
  triggerPhrases: string[];
};

export type AppToolDetail = AppTool & {
  appSlug: string;
  description: string | null;
  paramsJsonSchema: Record<string, unknown> | null;
  source: string | null;
  data: unknown;
  createdAt: string | null;
  expiresAt: number | null;
};

// `?as=<shareId>` rebinds the API to the share's owner namespace. The SPA
// reads it from the page URL (`/projects/<slug>?as=<shareId>`) and threads it
// through every project-scoped fetch so an invitee sees the OWNER's project
// instead of their own (empty) namespace.
function asQuery(shareId?: string | null): string {
  return shareId ? `?as=${encodeURIComponent(shareId)}` : "";
}

export const listApps = () => api<App[]>("/api/apps");
export const createApp = (body: { name: string; icon?: string; accentColor?: string }) =>
  api<App>("/api/apps", { method: "POST", body: JSON.stringify(body) });
export const getApp = (slug: string, shareId?: string | null) =>
  api<App>(`/api/apps/${slug}${asQuery(shareId)}`);
export const deleteApp = (slug: string) =>
  api<void>(`/api/apps/${slug}`, { method: "DELETE" });
export const listAppTools = (slug: string, shareId?: string | null) =>
  api<AppTool[]>(`/api/apps/${slug}/tools${asQuery(shareId)}`);
export const getAppTool = (slug: string, toolSlug: string, shareId?: string | null) =>
  api<AppToolDetail>(`/api/apps/${slug}/tools/${toolSlug}${asQuery(shareId)}`);
export const deleteAppTool = (slug: string, toolSlug: string, shareId?: string | null) =>
  api<void>(`/api/apps/${slug}/tools/${toolSlug}${asQuery(shareId)}`, { method: "DELETE" });
export const updateAppTool = (
  slug: string,
  toolSlug: string,
  body: {
    description?: string;
    displayMode?: string;
    exposed?: boolean;
    triggerPhrases?: string[];
  },
  shareId?: string | null,
) =>
  api<AppTool>(`/api/apps/${slug}/tools/${toolSlug}${asQuery(shareId)}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });

export type WidgetVersion = {
  version: number;
  changeNote: string | null;
  createdAt: string | null;
};
export type WidgetVersions = { current: number; versions: WidgetVersion[] };

export const listToolVersions = (slug: string, toolSlug: string) =>
  api<WidgetVersions>(`/api/apps/${slug}/tools/${toolSlug}/versions`);
export const restoreToolVersion = (slug: string, toolSlug: string, version: number) =>
  api<{ version: number; restoredFrom: number }>(
    `/api/apps/${slug}/tools/${toolSlug}/restore`,
    { method: "POST", body: JSON.stringify({ version }) },
  );

// Publish / unpublish a project as a read-only public gallery.
export const publishProject = (slug: string) =>
  api<{ slug: string; public: boolean; shareToken: string }>(
    `/api/apps/${slug}/publish`,
    { method: "POST" },
  );
export const unpublishProject = (slug: string) =>
  api<{ slug: string; public: boolean }>(`/api/apps/${slug}/unpublish`, {
    method: "POST",
  });

export type PublicTool = {
  toolSlug: string;
  description: string | null;
  htmlUrl: string | null;
  updatedAt: string | null;
  renderable: boolean;
};
export type PublicProjectData = {
  project: { name: string; slug: string; icon: string | null; accentColor: string | null };
  tools: PublicTool[];
};

// Unauthenticated — the public gallery for a published project (no JWT).
// Note: CloudFront's SPA fallback rewrites a 404 from the API to 200+index.html,
// so a missing/revoked token comes back as HTML, not a clean 404. Treat any
// non-JSON response as "not available" so the page shows a clean error.
export async function getPublicProject(token: string): Promise<PublicProjectData> {
  const res = await fetch(`${ORIGIN}/api/public/${token}`);
  const ct = res.headers.get("content-type") || "";
  if (!res.ok || !ct.includes("application/json")) {
    throw new ApiError(res.ok ? 404 : res.status, "This shared link isn't available");
  }
  return res.json() as Promise<PublicProjectData>;
}

// Live data — per-project fetch allowlist + BYOK secrets (values write-only).
export type ProjectSecret = { name: string; updatedAt: string | null };
export const listSecrets = (slug: string) =>
  api<{ secrets: ProjectSecret[] }>(`/api/apps/${slug}/secrets`);
export const setSecret = (slug: string, name: string, value: string) =>
  api<{ name: string; set: boolean }>(`/api/apps/${slug}/secrets`, {
    method: "POST",
    body: JSON.stringify({ name, value }),
  });
export const deleteSecret = (slug: string, name: string) =>
  api<void>(`/api/apps/${slug}/secrets/${encodeURIComponent(name)}`, { method: "DELETE" });
export const setFetchAllowlist = (slug: string, allowlist: string[]) =>
  api<App>(`/api/apps/${slug}`, {
    method: "PATCH",
    body: JSON.stringify({ fetchAllowlist: allowlist }),
  });
// Pro-only: customize/remove the per-project widget attribution footer.
export const updateWidgetFooter = (
  slug: string,
  body: { footerText?: string; footerHidden?: boolean },
) =>
  api<App>(`/api/apps/${slug}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });

// Usage analytics — Pro-gated. GET returns 402 (ProRequiredError) for Free
// users; the `tools` array is sorted by `total` desc and only includes tools
// called in the window (may be empty).
export type UsageDailyPoint = { date: string; count: number };
export type ToolUsage = {
  toolSlug: string;
  total: number;
  publicTotal: number;
  lastCalledAt: string | null;
  /** Oldest → newest, one entry per day in the window. */
  daily: UsageDailyPoint[];
};
export type ProjectUsage = {
  projectSlug: string;
  windowDays: number;
  totalCalls: number;
  publicCalls: number;
  tools: ToolUsage[];
};

export async function getProjectUsage(
  slug: string,
  days?: number,
): Promise<ProjectUsage> {
  const qs = days ? `?days=${days}` : "";
  return api<ProjectUsage>(`/api/apps/${slug}/usage${qs}`);
}

export type SaveToAppResponse = {
  appSlug: string;
  toolSlug: string;
  htmlUrl: string | null;
  bundleUrl: string | null;
};

export const saveToolToApp = (
  sourceAppSlug: string,
  sourceToolSlug: string,
  body: { targetAppSlug?: string; targetAppName?: string; targetToolSlug?: string; description?: string },
  opts: { asShareId?: string } = {},
) => {
  const qs = opts.asShareId ? `?as=${encodeURIComponent(opts.asShareId)}` : "";
  return api<SaveToAppResponse>(
    `/api/apps/${sourceAppSlug}/tools/${sourceToolSlug}/save-to-app${qs}`,
    { method: "POST", body: JSON.stringify(body) },
  );
};

// --- Project Teams --------------------------------------------------------

export type SharePermission = "read" | "write";

export type ProjectMember = {
  inviteeEmail: string;
  toolSlug?: string | null;
  permission: SharePermission;
  invitedAt: string;
  shareId: string;
  /** True once the invitee has signed in at least once. */
  accepted: boolean;
  /** Last sign-in timestamp (ms since epoch), or null if never signed in. */
  lastLoginAt: number | null;
};

export type ViewerShare = {
  shareId: string;
  ownerEmail: string;
  projectSlug: string;
  toolSlug?: string | null;
  permission: SharePermission;
  invitedAt: string;
};

export const listMembers = (slug: string) =>
  api<{ members: ProjectMember[] }>(`/api/apps/${slug}/members`);

export const inviteMember = (
  slug: string,
  body: { email: string; permission: SharePermission; toolSlug?: string },
) =>
  api<{ shareId: string }>(`/api/apps/${slug}/members`, {
    method: "POST",
    body: JSON.stringify(body),
  });

export const revokeMember = (
  slug: string,
  memberEmail: string,
  toolSlug?: string,
) => {
  const qs = toolSlug ? `?toolSlug=${encodeURIComponent(toolSlug)}` : "";
  return api<void>(
    `/api/apps/${slug}/members/${encodeURIComponent(memberEmail)}${qs}`,
    { method: "DELETE" },
  );
};

export const listShared = () => api<{ shares: ViewerShare[] }>(`/api/shared`);
